'use client';

import {ArrowLeftOutlined, ArrowRightOutlined} from '@ant-design/icons';
import Link from 'next/link';
import {type FC, memo, type PropsWithChildren, type ReactElement, useCallback, useContext, useMemo} from 'react';
import {StorageImageNew} from '@/components/atoms/StorageImage';
import type {Celebrity} from '@/entities/Celebrity/type/celebrities';
import ShowcaseContextNew from './ShowcaseContextNew';

interface CarouselProps {
  celebrities: Celebrity[];
}

interface NavigationButtonProps {
  direction: 'left' | 'right';
  onClick: () => void;
}

const NavigationButton: FC<NavigationButtonProps> = memo(
  ({direction, onClick}) => {
    const Icon = direction === 'left' ? ArrowLeftOutlined : ArrowRightOutlined;
    // const position = direction === 'left' ? 'left-[230px]' : 'right-[230px]';

    return (
      <button
        className={`hidden md:inline-flex z-10 rounded-full bg-white p-3 text-[#1A094E] transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#DA3D15]`}
        onClick={onClick}
        aria-label={direction === 'left' ? 'Предыдущий' : 'Следующий'}
      >
        <Icon className="text-[24px]" />
      </button>
    );
  },
  () => true,
);

NavigationButton.displayName = 'NavigationButton';

export const CarouselNew: FC<PropsWithChildren<CarouselProps>> = memo(
  ({celebrities, children}): ReactElement | null => {
    const {activeIdx, setActiveIdx} = useContext(ShowcaseContextNew);

    const celebrity = useMemo(() => celebrities[activeIdx], [celebrities, activeIdx]);
    const {id, first_name, last_name, main_screen_img} = celebrity;

    const handlePrevious = useCallback(() => {
      setActiveIdx(activeIdx > 0 ? activeIdx - 1 : celebrities.length - 1);
    }, [activeIdx, celebrities.length, setActiveIdx]);

    const handleNext = useCallback(() => {
      setActiveIdx(activeIdx < celebrities.length - 1 ? activeIdx + 1 : 0);
    }, [activeIdx, celebrities.length, setActiveIdx]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
          handlePrevious();
        } else if (e.key === 'ArrowRight') {
          handleNext();
        }
      },
      [handlePrevious, handleNext],
    );

    return (
      <div
        className="relative flex aspect-[1400/790] flex-col justify-end"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-label="Карусель знаменитостей"
      >
        <Link
          href={`/partners/${id}`}
          className="absolute inset-0 -mx-[30px] overflow-hidden focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#DA3D15]"
          aria-label={`Перейти к профилю ${first_name} ${last_name}`}
        >
          <StorageImageNew
            src={main_screen_img ?? celebrities[0].main_screen_img ?? ''}
            alt={`${first_name} ${last_name}`}
            className="w-full h-full object-cover"
            withBlur
            priority={true}
            aspectRatio="1400/790"
            sizes="(min-width: 1280px) 1392px, 100vw"
            quality={90}
            resize="cover"
            sources={[
              {
                media: '(min-width: 1280px)',
                width: 1392,
                height: 640,
                resize: 'cover',
                dprs: [1, 2],
                quality: 90,
              },
              {
                media: '(min-width: 768px)',
                width: 1024,
                height: 471,
                resize: 'cover',
                dprs: [1, 2],
                quality: 85,
              },
              {
                media: '(max-width: 767px)',
                width: 768,
                height: 354,
                resize: 'cover',
                dprs: [1, 2],
                quality: 80,
              },
            ]}
          />
        </Link>

        <div className="absolute px-4 lg:px-0 bottom-[317px] lg:bottom-[427px] left-1/2 -translate-x-1/2 lg:max-w-[1200px] w-full flex justify-between">
          <NavigationButton direction="left" onClick={handlePrevious} />
          <NavigationButton direction="right" onClick={handleNext} />
        </div>

        <div className="relative z-10 pb-[15px] md:pb-[70px]">{children}</div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.celebrities.length !== nextProps.celebrities.length) {
      return false;
    }

    for (let i = 0; i < prevProps.celebrities.length; i++) {
      if (prevProps.celebrities[i].id !== nextProps.celebrities[i].id) {
        return false;
      }
    }

    return true;
  },
);

CarouselNew.displayName = 'CarouselNew';
